import { ComponentRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { Subject } from 'rxjs';
import { memoize } from 'lodash-es';
import { ControlTypeGroup } from './cp-configuration/view-model';

import { AbstractControlComponent } from './cp-controls/abstract-control';
import { CpAutocompleteComponent } from './cp-controls/autocomplete/autocomplete.component';
import { CpComboComponent } from './cp-controls/combo/combo.component';
import { CpDatetimeComponent } from './cp-controls/datetime/datetime.component';
import { CpMultiStageMapWrapperComponent } from './cp-controls/multistage-map-wrapper/multistage-map-wrapper.component';
import { CpGenericComponent } from './cp-controls/generic/generic.component';
import { CpGridComponent } from './cp-controls/grid/grid.component';
import { CpImportComponent } from './cp-controls/import/import.component';
import { CpInputComponent } from './cp-controls/input/input.component';
import { CpMapWrapperComponent } from './cp-controls/map-wrapper/map-wrapper.component';
import { CpMeasureComponent } from './cp-controls/measure/measure.component';
import { CpNumericComponent } from './cp-controls/numeric/numeric.component';
import { CpPlaceholderComponent } from './cp-controls/placeholder/placeholder.component';
import { CpRadioComponent } from './cp-controls/radio/radio.component';
import { CpSelectorComponent } from './cp-controls/selector/selector.component';
import { CpTextEditorComponent } from './cp-controls/text-editor/text-editor.component';
import { CpUserControlComponent } from './cp-controls/user-control/user-control.component';
import { CpGraphComponent } from './cp-controls/graph/graph.component';
import { CpGridDefaultGroupsComponent } from './cp-controls/grid-default-groups/grid-default-groups.component';
import { IControlServiceCheck } from './control-check';
import { findControlWithSingleDataBinding } from './helpers';

const controlTypeGroupToComponent = memoize<(c: ControlTypeGroup) => Type<AbstractControlComponent>>(
    (controlTypeGroup: ControlTypeGroup) => {
        switch (controlTypeGroup) {
            case 'Input':
                return CpInputComponent;
            case 'Numeric':
                return CpNumericComponent;
            case 'Combo':
                return CpComboComponent;
            case 'TextEditor':
                return CpTextEditorComponent;
            case 'MapContainer':
                return CpMapWrapperComponent;
            case 'MultiStageMap':
                return CpMultiStageMapWrapperComponent;
            case 'Autocomplete':
                return CpAutocompleteComponent;
            case 'DateTime':
                return CpDatetimeComponent;
            case 'Generic':
                return CpGenericComponent;
            case 'Radio':
                return CpRadioComponent;
            case 'Grid':
                return CpGridComponent;
            case 'Selector':
                return CpSelectorComponent;
            case 'UserControl':
                return CpUserControlComponent;
            case 'Import':
                return CpImportComponent;
            case 'Measure':
                return CpMeasureComponent;
            case 'Graph':
                return CpGraphComponent;
            case 'GridDefaultGroups':
                return CpGridDefaultGroupsComponent;
            default:
                return CpPlaceholderComponent;
        }
    },
);

@Injectable({
    providedIn: 'root',
})
export class CpControlsService implements IControlServiceCheck {
    private readonly initializedSubject = new Subject<AbstractControlComponent>();

    private controlsByGrid: Record<string, ComponentRef<AbstractControlComponent>[]> = {};

    get componentInitialized$() {
        return this.initializedSubject.asObservable();
    }

    byGridId(id: string) {
        return this.controlsByGrid[id] ?? [];
    }

    initialized(component: AbstractControlComponent) {
        this.initializedSubject.next(component);
    }

    create(
        viewContainer: ViewContainerRef,
        controlTypeGroup: ControlTypeGroup,
        gridId: string,
    ): ComponentRef<AbstractControlComponent> {
        const componentConstructor = controlTypeGroupToComponent(controlTypeGroup);
        const component = viewContainer.createComponent<AbstractControlComponent>(componentConstructor);

        if (!this.controlsByGrid[gridId]) {
            this.controlsByGrid[gridId] = [];
        }
        this.controlsByGrid[gridId].push(component);

        return component;
    }

    destroyGrid(id: string) {
        delete this.controlsByGrid[id];
    }

    safeExit() {
        this.controlsByGrid = {};
    }

    controlWithSingleDataBindingExists(binding: string) {
        return findControlWithSingleDataBinding(binding, this.controlsByGrid);
    }
}
